import React, { useEffect, useMemo, useState } from "react"
import { Container, Offcanvas, OffcanvasBody } from "reactstrap"
import { Link } from "react-router-dom"
import Select from "react-select"
import moment from "moment"
import { DatePicker } from "antd"
import ReactApexChart from "react-apexcharts"
import { fetchNodeData } from "utils/common-fetchData"
import configure from "configure"
import "./expenseAnalysis.css"

const { RangePicker } = DatePicker

// Reports > Expense Analysis — "where is my money going".
// Data: GET expenseAnalysis (Backend-India controller/finance/expenseAnalysisController.js).

const LOCATION = configure.location[0]
const SYMBOL = LOCATION === 3 ? "$" : "₹"
const BRAND = "#762b98"

// Fixed categorical order (validated palette). Categories past the 7th fold
// into "Other" so no hue is ever generated or reused.
const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6"]
const OTHER_COLOR = "#9a9a96"
const MAX_SERIES = SERIES_COLORS.length

const BASIS_OPTIONS = [
  {
    value: "paid",
    label: "Payments made",
    hint: "Money actually paid out, counted on the date it was paid. Unpaid expenses are not included.",
  },
  {
    value: "expense",
    label: "Expenses booked",
    hint: "Every expense entered, counted on its expense date, whether it has been paid or not.",
  },
]

const generateFYOptions = () => {
  const now = new Date()
  const currentFYStart = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  const options = []
  for (let y = currentFYStart; y >= 2018; y--) {
    options.push({ label: `FY ${y}-${String(y + 1).slice(-2)}`, value: { from: `${y}-04-01`, to: `${y + 1}-03-31` } })
  }
  return options
}
const FY_OPTIONS = generateFYOptions()

// A rise above this is called out in red.
const RISE_ALERT_PCT = 20

const compact = n => {
  const v = Math.abs(Number(n) || 0)
  const sign = n < 0 ? "-" : ""
  if (LOCATION !== 3) {
    if (v >= 1e7) return `${sign}${SYMBOL}${(v / 1e7).toFixed(2)} Cr`
    if (v >= 1e5) return `${sign}${SYMBOL}${(v / 1e5).toFixed(2)} L`
  } else if (v >= 1e6) return `${sign}${SYMBOL}${(v / 1e6).toFixed(2)}M`
  if (v >= 1e3) return `${sign}${SYMBOL}${(v / 1e3).toFixed(1)}K`
  return `${sign}${SYMBOL}${v.toFixed(0)}`
}
const exact = n =>
  `${SYMBOL}${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const day = d => (d && moment(d).isValid() ? moment(d).format("DD MMM YYYY") : "—")

const selectStyles = {
  control: base => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderColor: "#e7e7ef",
    boxShadow: "none",
    minWidth: 140,
    "&:hover": { borderColor: BRAND },
  }),
  option: (base, s) => ({
    ...base,
    fontSize: 13,
    background: s.isSelected ? BRAND : s.isFocused ? "#f5eefa" : "#fff",
  }),
  singleValue: base => ({ ...base, fontSize: 13, fontWeight: 600 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: base => ({ ...base, zIndex: 20, borderRadius: 10, overflow: "hidden" }),
}

// ---------- small presentational pieces ----------

const DeltaChip = ({ value, suffix = "", invert = false }) => {
  if (value === null || value === undefined) return <span className="ea-chip neutral">New</span>
  const up = value > 0
  // For spend, up is bad — invert flips that for metrics where up is good.
  const tone = Math.abs(value) < 0.05 ? "neutral" : (up !== invert) ? (value > RISE_ALERT_PCT ? "bad-strong" : "bad") : "good"
  return (
    <span className={`ea-chip ${tone}`}>
      <i className={`bx ${up ? "bx-trending-up" : "bx-trending-down"}`} />
      {Math.abs(value).toFixed(1)}%{suffix}
    </span>
  )
}

const Sparkline = ({ values, color, height = 28, width = 96, fill = true }) => {
  if (!values?.length) return null
  const max = Math.max(...values, 1)
  const step = values.length > 1 ? width / (values.length - 1) : width
  const pts = values.map((v, i) => [i * step, height - 2 - (v / max) * (height - 4)])
  const line = pts.map(p => p.join(",")).join(" ")
  return (
    <svg className="ea-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      {fill && <polygon points={`0,${height} ${line} ${width},${height}`} fill={color} opacity="0.12" />}
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

const Kpi = ({ icon, tone, label, value, foot, children }) => (
  <div className="ea-kpi">
    <div className="ea-kpi-top">
      <span className={`ea-kpi-icon ${tone}`}>
        <i className={`bx ${icon}`} />
      </span>
      <span className="ea-kpi-label">{label}</span>
    </div>
    <div className="ea-kpi-value">{value}</div>
    {children}
    {foot && <div className="ea-kpi-foot">{foot}</div>}
  </div>
)

const Skeleton = () => (
  <div className="ea-skeleton">
    <div className="ea-kpis">
      <div className="ea-sk ea-sk-hero" />
      {[0, 1, 2].map(i => (
        <div key={i} className="ea-sk ea-sk-kpi" />
      ))}
    </div>
    <div className="ea-grid-main">
      <div className="ea-sk ea-sk-panel" />
      <div className="ea-sk ea-sk-panel" />
    </div>
  </div>
)

// ---------- page ----------

const ExpenseAnalysis = () => {
  document.title = "Expense Analysis | Zithas Technologies"

  const [selectedFY, setSelectedFY] = useState(FY_OPTIONS[0])
  const [customRange, setCustomRange] = useState(null)
  const [basis, setBasis] = useState(BASIS_OPTIONS[0])
  const [trendMode, setTrendMode] = useState("category")
  const [data, setData] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [isLoading, setLoading] = useState(true)
  const [drill, setDrill] = useState(null) // { category, rows, loading }

  const range = useMemo(
    () =>
      customRange
        ? { from: customRange[0].startOf("month").format("YYYY-MM-DD"), to: customRange[1].endOf("month").format("YYYY-MM-DD") }
        : selectedFY.value,
    [customRange, selectedFY]
  )
  const query = `from=${range.from}&to=${range.to}&location=${LOCATION}&basis=${basis.value}`

  useEffect(() => {
    let cancelled = false
    fetchNodeData(setLoading, `expenseAnalysis?${query}`).then(res => {
      if (cancelled) return
      setLoadFailed(!res?.success)
      if (res?.success) setData(res.data)
    })
    return () => {
      cancelled = true
    }
  }, [query])

  const openCategory = async category => {
    setDrill({ category, rows: [], loading: true })
    const id = category.category_id === null ? "none" : category.category_id
    const res = await fetchNodeData(null, `expenseAnalysis/category/${id}?${query}`)
    setDrill(d => (d && d.category === category ? { category, rows: res?.success ? res.data : [], loading: false } : d))
  }

  const summary = data?.summary
  const categories = useMemo(() => data?.categories || [], [data])
  const spending = useMemo(() => categories.filter(c => c.total > 0), [categories])
  const months = useMemo(() => data?.months || [], [data])

  const colorOf = useMemo(() => {
    const map = new Map()
    spending.slice(0, MAX_SERIES).forEach((c, i) => map.set(String(c.category_id), SERIES_COLORS[i]))
    return id => map.get(String(id)) || OTHER_COLOR
  }, [spending])

  // month -> category -> total
  const monthlyOf = useMemo(() => {
    const cell = new Map((data?.monthly || []).map(r => [`${r.month}|${r.category_id}`, r.total]))
    return id => months.map(m => cell.get(`${m}|${id}`) || 0)
  }, [data, months])

  const monthTotals = useMemo(
    () => months.map(m => (data?.monthly || []).filter(r => r.month === m).reduce((s, r) => s + r.total, 0)),
    [data, months]
  )

  // Share bar: top categories + Other.
  const shareSegments = useMemo(() => {
    const total = summary?.total || 0
    if (!total) return []
    const top = spending.slice(0, MAX_SERIES).map(c => ({ name: c.name, value: c.total, color: colorOf(c.category_id) }))
    const rest = spending.slice(MAX_SERIES).reduce((s, c) => s + c.total, 0)
    if (rest > 0) top.push({ name: "Other", value: rest, color: OTHER_COLOR })
    return top.map(s => ({ ...s, pct: (s.value / total) * 100 }))
  }, [spending, summary, colorOf])

  // Plain-language callouts derived from the numbers.
  const insights = useMemo(() => {
    if (!summary || !spending.length) return []
    const list = []
    const total = summary.total || 1
    const riser = spending
      .filter(c => c.previous > 0 && c.change_pct > RISE_ALERT_PCT && c.share >= 2)
      .sort((a, b) => b.total - b.previous - (a.total - a.previous))[0]
    if (riser) {
      list.push({
        tone: "bad",
        icon: "bx-trending-up",
        title: `${riser.name} is up ${riser.change_pct.toFixed(0)}%`,
        text: `${compact(riser.previous)} → ${compact(riser.total)} compared with the previous period.`,
        onClick: () => openCategory(riser),
      })
    }
    const top3 = spending.slice(0, 3)
    const top3Share = (top3.reduce((s, c) => s + c.total, 0) / total) * 100
    list.push({
      tone: "info",
      icon: "bx-pie-chart-alt-2",
      title: `Top ${top3.length} categor${top3.length === 1 ? "y" : "ies"} = ${top3Share.toFixed(0)}% of spend`,
      text: top3.map(c => c.name).join(", "),
    })
    if (summary.recurring_total > 0) {
      list.push({
        tone: "neutral",
        icon: "bx-repeat",
        title: `${((summary.recurring_total / total) * 100).toFixed(0)}% is fixed recurring spend`,
        text: `${compact(summary.recurring_total)} committed regardless of activity.`,
      })
    }
    if (summary.unrecorded?.total > 0) {
      list.push({
        tone: "warn",
        icon: "bx-error",
        title: `${compact(summary.unrecorded.total)} not recorded as expenses`,
        text: `${summary.unrecorded.count} bank / card debits are still unmatched.`,
        link: `${configure.appUrl}reports/bank-statement-console`,
      })
    }
    return list.slice(0, 4)
  }, [summary, spending])

  const trend = useMemo(() => {
    if (!data) return null
    const labels = months.map(m => moment(m, "YYYY-MM").format("MMM YY"))
    const base = {
      chart: { toolbar: { show: false }, fontFamily: "inherit", animations: { speed: 400 }, zoom: { enabled: false } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#eef0f4", strokeDashArray: 4, padding: { left: 6, right: 6 } },
      xaxis: { categories: labels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#98a2b3", fontSize: "11px" } } },
      yaxis: { labels: { formatter: v => compact(v), style: { colors: "#98a2b3", fontSize: "11px" } } },
      tooltip: { shared: true, intersect: false, y: { formatter: v => exact(v) } },
      legend: { position: "top", horizontalAlign: "left", fontSize: "12px", markers: { radius: 12, width: 9, height: 9 }, itemMargin: { horizontal: 8 } },
    }
    if (trendMode === "total") {
      return {
        type: "area",
        series: [{ name: "Total spend", data: monthTotals }],
        options: {
          ...base,
          chart: { ...base.chart, type: "area" },
          colors: [BRAND],
          stroke: { curve: "smooth", width: 2.5 },
          fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 95] } },
          markers: { size: 0, hover: { size: 5 } },
        },
      }
    }
    const shown = spending.slice(0, MAX_SERIES)
    const shownIds = new Set(shown.map(c => String(c.category_id)))
    const series = shown.map(c => ({ name: c.name, data: monthlyOf(c.category_id) }))
    if (spending.length > MAX_SERIES) {
      series.push({
        name: "Other",
        data: months.map(m =>
          data.monthly.filter(r => r.month === m && !shownIds.has(String(r.category_id))).reduce((s, r) => s + r.total, 0)
        ),
      })
    }
    return {
      type: "bar",
      series,
      options: {
        ...base,
        chart: { ...base.chart, type: "bar", stacked: true },
        colors: [...shown.map(c => colorOf(c.category_id)), OTHER_COLOR],
        plotOptions: { bar: { columnWidth: "52%", borderRadius: 4, borderRadiusApplication: "end", borderRadiusWhenStacked: "last" } },
        stroke: { show: true, width: 2, colors: ["#fff"] },
      },
    }
  }, [data, months, spending, colorOf, monthlyOf, monthTotals, trendMode])

  const exportCsv = () => {
    const rows = [
      ["Category", "This period", "Previous period", "Change %", "Share %", "Entries"],
      ...categories.map(c => [
        c.name,
        c.total.toFixed(2),
        c.previous.toFixed(2),
        c.change_pct === null ? "" : c.change_pct.toFixed(1),
        c.share.toFixed(1),
        c.count,
      ]),
    ]
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n")
    const link = document.createElement("a")
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
    link.download = `expense-analysis_${range.from}_${range.to}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const methodTotal = (data?.by_method || []).reduce((s, m) => s + m.total, 0) || 1
  const METHOD_COLORS = { Bank: "#2a78d6", "Credit Card": "#eb6834", Others: "#1baf7a", Unknown: OTHER_COLOR }
  const METHOD_ICONS = { Bank: "bx-building-house", "Credit Card": "bx-credit-card", Others: "bx-wallet", Unknown: "bx-help-circle" }
  const topCategory = spending[0]

  return (
    <div className="page-content ea-page">
      <Container fluid>
      {/* ---------- Header ---------- */}
      <div className="ea-header">
        <div>
          <div className="ea-eyebrow">Reports</div>
          <h1 className="ea-title">Expense Analysis</h1>
          <div className="ea-subtitle">
            Where your money went · {day(range.from)} – {day(range.to)}
            {isLoading && data && <span className="ea-refreshing"><i className="bx bx-loader-alt bx-spin" /> Updating</span>}
          </div>
        </div>
        <div className="ea-toolbar">
          <div className="ea-segment" role="tablist" aria-label="Count by">
            {BASIS_OPTIONS.map(o => (
              <button
                key={o.value}
                role="tab"
                aria-selected={basis.value === o.value}
                className={basis.value === o.value ? "active" : ""}
                title={o.hint}
                onClick={() => setBasis(o)}
              >
                {o.label}
              </button>
            ))}
          </div>
          <Select
            styles={selectStyles}
            options={FY_OPTIONS}
            value={selectedFY}
            isDisabled={!!customRange}
            onChange={setSelectedFY}
            isSearchable={false}
          />
          <RangePicker
            picker="month"
            className="ea-range"
            value={customRange}
            onChange={v => setCustomRange(v && v[0] && v[1] ? v : null)}
            format="MMM YY"
            placeholder={["From", "To"]}
          />
          <button className="ea-icon-btn" onClick={exportCsv} disabled={!categories.length} title="Export CSV">
            <i className="bx bx-download" />
          </button>
        </div>
      </div>

      <div className="ea-basis-note">
        <i className="bx bx-info-circle" />
        <span>
          <b>{basis.label}:</b> {basis.hint}
        </span>
      </div>

      {!data && isLoading && <Skeleton />}

      {loadFailed && !isLoading && (
        <div className="ea-empty">
          <i className="bx bx-cloud-lightning" />
          <h5>Couldn't load the analysis</h5>
          <p>Check your connection and change a filter to try again.</p>
        </div>
      )}

      {data && !spending.length && (
        <div className="ea-empty">
          <i className="bx bx-receipt" />
          <h5>No expenses in this period</h5>
          <p>Try another financial year, or switch between Paid and Incurred.</p>
        </div>
      )}

      {summary && spending.length > 0 && (
        <div className={isLoading ? "ea-dim" : ""}>
          {/* ---------- KPIs ---------- */}
          <div className="ea-kpis">
            <div className="ea-hero">
              <div className="ea-hero-label">Total spent</div>
              <div className="ea-hero-value">{compact(summary.total)}</div>
              <div className="ea-hero-meta">
                <DeltaChip value={summary.change_pct} />
                <span>vs {compact(summary.previous_total)} previous period</span>
              </div>
              <div className="ea-hero-spark">
                <Sparkline values={monthTotals} color="#ffffff" width={260} height={56} />
              </div>
            </div>
            <Kpi icon="bx-calendar" tone="blue" label="Average per month" value={compact(summary.avg_per_month)} foot={`Across ${months.length} month${months.length === 1 ? "" : "s"}`} />
            <Kpi
              icon="bx-repeat"
              tone="purple"
              label="Recurring (fixed)"
              value={compact(summary.recurring_total)}
              foot={`${((summary.recurring_total / (summary.total || 1)) * 100).toFixed(0)}% of total spend`}
            >
              <div className="ea-progress">
                <span style={{ width: `${Math.min((summary.recurring_total / (summary.total || 1)) * 100, 100)}%` }} />
              </div>
            </Kpi>
            {summary.unrecorded ? (
              <Kpi
                icon="bx-error"
                tone="amber"
                label="Unrecorded debits"
                value={compact(summary.unrecorded.total)}
                foot={
                  <Link to={`${configure.appUrl}reports/bank-statement-console`} className="ea-link">
                    {summary.unrecorded.count} unmatched entries · Reconcile <i className="bx bx-right-arrow-alt" />
                  </Link>
                }
              />
            ) : (
              <Kpi
                icon="bx-crown"
                tone="amber"
                label="Biggest category"
                value={topCategory?.name}
                foot={`${compact(topCategory?.total)} · ${topCategory?.share.toFixed(0)}% of spend`}
              />
            )}
          </div>

          {/* ---------- Insights ---------- */}
          {insights.length > 0 && (
            <div className="ea-insights">
              {insights.map((ins, i) => {
                const body = (
                  <>
                    <span className={`ea-insight-icon ${ins.tone}`}>
                      <i className={`bx ${ins.icon}`} />
                    </span>
                    <span>
                      <span className="ea-insight-title">{ins.title}</span>
                      <span className="ea-insight-text">{ins.text}</span>
                    </span>
                  </>
                )
                if (ins.link)
                  return (
                    <Link key={i} to={ins.link} className="ea-insight clickable">
                      {body}
                    </Link>
                  )
                return (
                  <button key={i} className={`ea-insight ${ins.onClick ? "clickable" : ""}`} onClick={ins.onClick} disabled={!ins.onClick}>
                    {body}
                  </button>
                )
              })}
            </div>
          )}

          {/* ---------- Trend + breakdown ---------- */}
          <div className="ea-grid-main">
            <section className="ea-panel">
              <div className="ea-panel-head">
                <div>
                  <h2>Spend over time</h2>
                  <p>Monthly {basis.value === "paid" ? "payments" : "expenses"}</p>
                </div>
                <div className="ea-segment small">
                  <button className={trendMode === "category" ? "active" : ""} onClick={() => setTrendMode("category")}>
                    By category
                  </button>
                  <button className={trendMode === "total" ? "active" : ""} onClick={() => setTrendMode("total")}>
                    Total
                  </button>
                </div>
              </div>
              {trend && <ReactApexChart key={trendMode} type={trend.type} height={340} series={trend.series} options={trend.options} />}
            </section>

            <section className="ea-panel">
              <div className="ea-panel-head">
                <div>
                  <h2>Where the money goes</h2>
                  <p>{spending.length} categories · click one for details</p>
                </div>
              </div>
              <div className="ea-sharebar" role="img" aria-label="Share of spend by category">
                {shareSegments.map(s => (
                  <span key={s.name} style={{ width: `${s.pct}%`, background: s.color }} title={`${s.name}: ${s.pct.toFixed(1)}%`} />
                ))}
              </div>
              <div className="ea-catlist">
                {spending.map((c, i) => (
                  <button key={String(c.category_id)} className="ea-cat" onClick={() => openCategory(c)}>
                    <span className="ea-cat-rank">{i + 1}</span>
                    <span className="ea-cat-main">
                      <span className="ea-cat-name">
                        <span className="ea-dot" style={{ background: colorOf(c.category_id) }} />
                        {c.name}
                      </span>
                      <span className="ea-cat-sub">
                        {c.share.toFixed(1)}% · {c.count} entr{c.count === 1 ? "y" : "ies"}
                      </span>
                    </span>
                    <Sparkline values={monthlyOf(c.category_id)} color={colorOf(c.category_id)} width={72} height={24} />
                    <span className="ea-cat-amount">
                      <span>{compact(c.total)}</span>
                      <DeltaChip value={c.change_pct} />
                    </span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* ---------- Largest spends + payment method ---------- */}
          <div className="ea-grid-bottom">
            <section className="ea-panel">
              <div className="ea-panel-head">
                <div>
                  <h2>Largest {basis.value === "paid" ? "payments" : "expenses"}</h2>
                  <p>Top 10 single entries in this period</p>
                </div>
              </div>
              <div className="ea-toplist">
                {data.top.map((t, i) => (
                  <div key={i} className="ea-top">
                    <span className="ea-avatar" style={{ background: `${colorOf(t.category_id)}1f`, color: colorOf(t.category_id) }}>
                      {(t.title || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="ea-top-main">
                      <span className="ea-top-title">{t.title}</span>
                      <span className="ea-top-sub">
                        {t.category} · {day(t.date)}
                      </span>
                    </span>
                    <span className="ea-top-amount">{exact(t.amount)}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="ea-panel">
              <div className="ea-panel-head">
                <div>
                  <h2>How it was paid</h2>
                  <p>Split by payment method</p>
                </div>
              </div>
              <div className="ea-sharebar tall">
                {data.by_method.map(m => (
                  <span key={m.method} style={{ width: `${(m.total / methodTotal) * 100}%`, background: METHOD_COLORS[m.method] || OTHER_COLOR }} />
                ))}
              </div>
              <div className="ea-methods">
                {data.by_method.map(m => (
                  <div key={m.method} className="ea-method">
                    <span className="ea-method-icon" style={{ color: METHOD_COLORS[m.method] || OTHER_COLOR }}>
                      <i className={`bx ${METHOD_ICONS[m.method] || "bx-wallet"}`} />
                    </span>
                    <span className="ea-method-name">{m.method}</span>
                    <span className="ea-method-pct">{((m.total / methodTotal) * 100).toFixed(1)}%</span>
                    <span className="ea-method-amount">{compact(m.total)}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      )}

      </Container>

      {/* ---------- Category drawer ---------- */}
      <Offcanvas isOpen={!!drill} toggle={() => setDrill(null)} direction="end" className="ea-drawer">
        {drill && (
          <>
            <div className="ea-drawer-head">
              <div>
                <div className="ea-eyebrow">
                  <span className="ea-dot" style={{ background: colorOf(drill.category.category_id) }} />
                  Category
                </div>
                <h3>{drill.category.name}</h3>
              </div>
              <button className="ea-icon-btn" onClick={() => setDrill(null)} aria-label="Close">
                <i className="bx bx-x" />
              </button>
            </div>
            <OffcanvasBody>
              <div className="ea-drawer-stats">
                <div>
                  <span>This period</span>
                  <b>{exact(drill.category.total)}</b>
                </div>
                <div>
                  <span>Previous</span>
                  <b>{exact(drill.category.previous)}</b>
                </div>
                <div>
                  <span>Change</span>
                  <b><DeltaChip value={drill.category.change_pct} /></b>
                </div>
                <div>
                  <span>Share</span>
                  <b>{drill.category.share.toFixed(1)}%</b>
                </div>
              </div>
              <div className="ea-drawer-spark">
                <Sparkline values={monthlyOf(drill.category.category_id)} color={colorOf(drill.category.category_id)} width={380} height={64} />
              </div>
              <div className="ea-drawer-list-head">
                Entries <span>{drill.loading ? "" : drill.rows.length}</span>
              </div>
              {drill.loading ? (
                [0, 1, 2, 3].map(i => <div key={i} className="ea-sk ea-sk-row" />)
              ) : !drill.rows.length ? (
                <div className="ea-muted">No entries.</div>
              ) : (
                drill.rows.map((r, i) => (
                  <div key={i} className="ea-entry">
                    <span className="ea-entry-main">
                      <span className="ea-entry-title">
                        {r.title}
                        {r.recurring && <span className="ea-tag">Recurring</span>}
                      </span>
                      <span className="ea-entry-sub">
                        {day(r.date)} · {r.payment_method}
                      </span>
                    </span>
                    <span className="ea-entry-amount">{exact(r.amount)}</span>
                  </div>
                ))
              )}
            </OffcanvasBody>
          </>
        )}
      </Offcanvas>
    </div>
  )
}

export default ExpenseAnalysis
